import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  Clock,
  HelpCircle,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { fetchPublicStatus } from "../../services/statusApi";
import type {
  PublicStatusResult,
  PublicStatusState,
  StatusComponent,
} from "../../types/status";
import { StatusDetailsPanel } from "./StatusDetailsPanel";

const POLL_INTERVAL_MS = 60000;

const statusCopy: Record<
  PublicStatusState,
  { label: string; banner: string; className: string }
> = {
  operational: {
    label: "Operational",
    banner: "All systems operational",
    className: "bg-emerald-50 text-emerald-800 border-emerald-200",
  },
  degraded: {
    label: "Degraded",
    banner: "Some systems are degraded",
    className: "bg-amber-50 text-amber-800 border-amber-200",
  },
  outage: {
    label: "Outage",
    banner: "One or more systems are unavailable",
    className: "bg-red-50 text-red-800 border-red-200",
  },
  unknown: {
    label: "Unknown",
    banner: "System status is unknown",
    className: "bg-slate-50 text-slate-800 border-slate-200",
  },
};

export const StatusPage: React.FC = () => {
  const [status, setStatus] = useState<PublicStatusResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const loadStatus = async () => {
    try {
      setError(null);
      const nextStatus = await fetchPublicStatus();
      setStatus(nextStatus);
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : "Status feed unavailable",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadStatus();
    const interval = window.setInterval(() => {
      void loadStatus();
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, []);

  const overallStatus = status?.status || "unknown";
  const summary = statusCopy[overallStatus];
  const components = useMemo(
    () => status?.components || fallbackComponents(error),
    [error, status?.components],
  );

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="h-screen overflow-y-auto bg-slate-50 text-slate-900">
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-blue-700">
              Better Cities Company
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight">
              Vehicle Tracker Status
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Current availability for tracker services and GPS data intake.
            </p>
          </div>

          <button
            type="button"
            onClick={() => void loadStatus()}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-100"
          >
            <RefreshCw
              className={isLoading ? "h-4 w-4 animate-spin" : "h-4 w-4"}
            />
            Refresh
          </button>
        </header>

        <section
          className={`mb-6 rounded-2xl border p-6 shadow-sm ${summary.className}`}
        >
          <div className="flex items-start gap-4">
            {getStatusIcon(overallStatus, "h-8 w-8 flex-none")}
            <div>
              <h2 className="text-xl font-semibold">{summary.banner}</h2>
              <p className="mt-1 text-sm opacity-80">
                {status
                  ? `Last checked ${formatDateTime(status.checkedAt)}`
                  : "Waiting for the status feed."}
              </p>
              {error && (
                <p className="mt-3 rounded-lg bg-white/70 px-3 py-2 text-sm">
                  Status feed unavailable: {error}
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="grid gap-4">
          {components.map((component) => (
            <StatusCard
              key={component.id}
              component={component}
              isExpanded={expandedIds.has(component.id)}
              onToggle={() => toggleExpanded(component.id)}
            />
          ))}
        </section>

        <footer className="mt-8 flex items-center gap-2 text-xs text-slate-500">
          <Clock className="h-4 w-4" />
          This page refreshes automatically every 60 seconds.
        </footer>
      </main>
    </div>
  );
};

const StatusCard: React.FC<{
  component: StatusComponent;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ component, isExpanded, onToggle }) => {
  const copy = statusCopy[component.status];
  const hasDetails = Boolean(component.details);
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!hasDetails) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <article
      className={`rounded-xl border border-slate-200 bg-white shadow-sm ${
        hasDetails ? "cursor-pointer" : ""
      }`}
      role={hasDetails ? "button" : undefined}
      tabIndex={hasDetails ? 0 : undefined}
      aria-expanded={hasDetails ? isExpanded : undefined}
      onClick={hasDetails ? onToggle : undefined}
      onKeyDown={handleKeyDown}
    >
      <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          {hasDetails && (
            <ChevronDown
              className={`mt-0.5 h-5 w-5 flex-none text-slate-400 transition-transform ${
                isExpanded ? "rotate-180" : ""
              }`}
            />
          )}
          {getStatusIcon(component.status, "mt-0.5 h-5 w-5 flex-none")}
          <div>
            <h3 className="font-semibold text-slate-900">{component.name}</h3>
            <p className="mt-1 text-sm text-slate-600">
              {component.message || copy.label}
            </p>
            {component.incidentStartedAt && (
              <p className="mt-2 text-xs text-slate-500">
                Incident started {formatDateTime(component.incidentStartedAt)}
                {" · "}
                Duration {formatIncidentDuration(component)}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1 text-left sm:text-right">
          <span
            className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold self-end ${copy.className}`}
          >
            {copy.label}
          </span>
          <span className="text-xs text-slate-500">
            {formatDateTime(component.checkedAt)}
            {component.responseTimeMs !== undefined
              ? ` | ${component.responseTimeMs}ms`
              : ""}
          </span>
        </div>
      </div>

      {hasDetails && isExpanded && (
        <div className="border-t border-slate-100 px-5 py-4">
          <StatusDetailsPanel component={component} />
        </div>
      )}
    </article>
  );
};

function getStatusIcon(status: PublicStatusState, className: string) {
  switch (status) {
    case "operational":
      return <CheckCircle className={`${className} text-emerald-600`} />;
    case "degraded":
      return <AlertTriangle className={`${className} text-amber-600`} />;
    case "outage":
      return <XCircle className={`${className} text-red-600`} />;
    default:
      return <HelpCircle className={`${className} text-slate-500`} />;
  }
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatIncidentDuration(component: StatusComponent): string {
  const durationMs =
    component.incidentDurationMs ??
    (component.incidentStartedAt
      ? Date.now() - new Date(component.incidentStartedAt).getTime()
      : 0);

  const totalMinutes = Math.max(1, Math.round(durationMs / 60000));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 && days === 0) parts.push(`${minutes}m`);
  return parts.length > 0 ? parts.join(" ") : "1m";
}

function fallbackComponents(error: string | null): StatusComponent[] {
  return [
    {
      id: "status-feed",
      name: "Status Feed",
      status: error ? "outage" : "unknown",
      checkedAt: new Date().toISOString(),
      message: error || "Loading status feed",
    },
    {
      id: "frontend",
      name: "Frontend",
      status: "operational",
      checkedAt: new Date().toISOString(),
      message: "This status page is reachable",
    },
  ];
}

export default StatusPage;
